import { Http, HttpRequestConfig, HttpInstance } from '@okapi/http';
import { ApiException } from './api-exeption';

export type ApiRequestConfig = HttpRequestConfig & {
  orchestratorHost: string;
  useCredentials: boolean;
  requestID: string;
  apiKey?: string;
  formData?: boolean;
};

const SUCCESS_RESPONSE_STATUS_CODE_LIST = [200, 201, 202];

const getQueryParams = (init?: Record<string, string | undefined>): string => {
  const lang = 'ru';
  const queryParams = init ? { ...init, lang } : { lang };

  return new URLSearchParams(queryParams).toString();
}

export class Api {
  http: HttpInstance;

  constructor() {
    this.http = new Http({
      headers: {
        Accept: 'application/json, text/plain, */*',
      },
    });
  }

  public abortController = () => this.http.abortController();

  post = <Response = unknown, Data = unknown>(
    url: string,
    data: Data,
    config: ApiRequestConfig,
    queryParams?: Record<string, string | undefined>,
  ) => {
    const params = getQueryParams(queryParams);

    return this._doFetch<Response>(buildUrl(config.orchestratorHost, `${url}?${params}`), {
      method: 'POST',
      data,
      headers: config?.headers,
      ...config,
    })
  };

  get = <Response = unknown, Data extends Record<string, string> = Record<string, string>>(
    url: string,
    data: Data,
    config: ApiRequestConfig
  ) => {
    const params = new URLSearchParams(data).toString();
    const resultUrl = `${buildUrl(config.orchestratorHost, url)}?${params}`;

    return this._doFetch<Response>(resultUrl, { method: 'GET', ...config });
  };

  private _doFetch<Response>(url: string, config: ApiRequestConfig): Promise<Response> {
    const resultUrl = /^\/\//.test(url) ? `https:${url}` : url;
    const { useCredentials, requestID, apiKey, formData, ...init } = config;

    init.withCredentials = useCredentials;

    if (requestID) {
      init.headers = { ...init.headers, 'x-request-id': requestID };
    }

    if (apiKey) {
      init.headers = { ...init.headers, 'x-api-key': apiKey };
    }

    if (!formData) {
      init.headers = {
        ...init.headers,
        'Content-Type': 'application/json;charset=UTF-8',
      }
    }

    return this.http
      .request<Response>(resultUrl, init)
      .then(response =>
        SUCCESS_RESPONSE_STATUS_CODE_LIST.includes(response.status) ? response : Promise.reject(response)
      )
      .then(response => response.data)
      .catch(e => {
        const data = e.response ? e.response.data : e.data;

        throw new ApiException(e.status, data?.message);
      });
  }
}

export const api = new Api();

function buildUrl(base: string, part: string) {
  return new URL(part, base).toString();
}
